// Copyright (c) Docugami, Inc. All rights reserved.

import { IGroup } from "@fluentui/react";
import { Dictionary, EntityState, createSelector } from "@reduxjs/toolkit";
import { createFolderId } from "components/file_management/file_helpers";
import { filterByCollectionKey } from "hooks/collectionKeyFilter";
import { ReportViewMode } from "hooks/navigationService";
import { ClusterDocument, DocumentStatus } from "networking/api";
import { DocumentEvaluationStatus, FlaggedResponse, GetReportBindingStatsResponse, ReportBinding, ReportBindingEvaluation, ReportBindingEvaluationPreview, ReportDocumentState, ReportEntry, ReportEntryGroup, ReportViewContext } from "networking/reportsApi";
import { AppState } from "store";
import { frontendSlice } from "store/frontend/frontendSlice";
import { OperationMetadata, ReportFilter, ReportFilterAxis, reportDocumentsAdapter, selectAllDocuments, selectAllEntries, selectAllGroups, selectAllReportRemoteOperations, selectFlaggedDocumentIds, selectGetBindingsState, selectGetEvaluationsPreviewState, selectGetReportSchemaState, selectGetPublishedDocumentsState, selectPatchedReportBindingsState, selectPatchedReportSchemaState, selectGetReviewBindingStatsState, selectGetReviewDocumentsState } from "store/reports/reportsSlice";
import { getClusterById } from "store/state_helpers";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

export const selectOrderedReportEntries = createSelector(
    (state: AppState) => selectPatchedReportSchemaState(state, selectReportId(state)),
    (state): ReportEntry[] => {
        if (!state) {
            return []
        }

        return state.report.entries.map(x => state.entries.entities[x]).filter(x => !!x) as ReportEntry[];
    }
);

export const selectSearchResultReportEntries = createSelector(
    (state: AppState, reportId: string) => selectPatchedReportSchemaState(state, reportId),
    (state: AppState, reportId: string) => state.reportsSlice.filterAxis === ReportFilterAxis.Items ? state.reportsSlice.searchString : undefined,
    (state, titleSearchString): ReportEntry[] => {
        if (!state) {
            return []
        }

        let entries = state.report.entries.map(x => state.entries.entities[x]).filter(x => !!x) as ReportEntry[];
        if (!!titleSearchString) {
            entries = filterByCollectionKey(titleSearchString, entries, ['title']);
        }

        return entries;
    }
);

// This selects the filtered report entries against the full documents view
export const createSelectFilteredReportEntries = () => createSelector(
    (state: AppState, reportId: string, context?: ReportViewContext) => selectSearchResultReportEntries(state, reportId),
    selectReportEntriesFilterState,
    (state: AppState, reportId: string, context?: ReportViewContext) => state.reportsSlice.activeFilters,
    (state: AppState, reportId: string, context?: ReportViewContext) => state.reportsSlice.filterAxis,
    (entries, entryToFilterState, activeFilters, filterAxis) => {
        if (filterAxis !== ReportFilterAxis.Items) {
            return entries;
        }

        return getFilteredItemsFromLookup(entries, entry => entry.id, activeFilters, entryToFilterState);
    }
);

// This selects the filtered report entries against the current report document
export const createSelectFilteredReportEntriesForDocument = () => createSelector(
    (state: AppState, reportId: string, context?: ReportViewContext) => selectSearchResultReportEntries(state, reportId),
    selectReportEntriesFilterStateForDocument,
    (state: AppState, reportId: string, context?: ReportViewContext) => state.reportsSlice.activeFilters,
    (state: AppState, reportId: string, context?: ReportViewContext) => state.reportsSlice.filterAxis,
    (entries, entryToFilterState, activeFilters, filterAxis) => {
        if (filterAxis !== ReportFilterAxis.Items) {
            return entries;
        }

        return getFilteredItemsFromLookup(entries, entry => entry.id, activeFilters, entryToFilterState);
    }
);

type FilterStateLookup = { [id: string]: ReportAxisFilterState };

interface ReportAxisFilterState {
    fullyConfirmed: boolean; // All entry bindings are confirmed for this item across the opposite axis
    flagged: boolean; // At least one flagged binding exists for this item across the opposite axis
}

// Helper to get the filtered list of items given the active report filters, and a filter state lookup for each item across the opposite report axis set
function getFilteredItemsFromLookup<T>(items: T[], idSelector: (item: T) => string, activeFilters: ReportFilter[], filterStateLookup: FilterStateLookup) {
    if (!activeFilters.length) {
        return items;
    }

    const showUnconfirmed = activeFilters.includes(ReportFilter.Unconfirmed);
    const showConfirmed = activeFilters.includes(ReportFilter.Confirmed);
    const showFlagged = activeFilters.includes(ReportFilter.Flagged);

    return items.filter(item => {
        const { fullyConfirmed, flagged } = filterStateLookup[idSelector(item)];

        const showDueToNotBeingConfirmed = showUnconfirmed && !fullyConfirmed;
        const showDueToBeingConfirmed = showConfirmed && fullyConfirmed;
        const showDueToBeingFlagged = showFlagged && flagged

        return showDueToNotBeingConfirmed ||
            showDueToBeingConfirmed ||
            showDueToBeingFlagged
    })
}

// This is the filtered entries state lookup selector for the full report documents view
// Returns the ReportAxisFilterState for the given entries, against all documents in the current report view
const selectReportEntriesFilterState = createSelector(
    (state: AppState, reportId: string, context?: ReportViewContext) => selectSearchResultReportEntries(state, reportId),
    (state: AppState, reportId: string, context?: ReportViewContext) => context === ReportViewContext.Review ? selectGetReviewBindingStatsState(state, reportId) : undefined,
    (state: AppState, reportId: string, context?: ReportViewContext) => selectReportDocumentsView(state, context)?.ids,
    (state: AppState, reportId: string, context?: ReportViewContext) => selectFlaggedDocumentIds(state, reportId),
    (state: AppState, reportId: string, context?: ReportViewContext) => context,
    getEntryToFilterStateLookup
);

// This is the filtered entries state lookup selector for a single document view
// Returns the ReportAxisFilterState for the given entries, against the current report document
const selectReportEntriesFilterStateForDocument = createSelector(
    (state: AppState, reportId: string, context?: ReportViewContext) => selectSearchResultReportEntries(state, reportId),
    (state: AppState, reportId: string, context?: ReportViewContext) => context === ReportViewContext.Review ? selectGetReviewBindingStatsState(state, reportId) : undefined,
    (state: AppState, reportId: string, context?: ReportViewContext) => selectReportDocumentId(state),
    (state: AppState, reportId: string, context?: ReportViewContext) => selectFlaggedDocumentIds(state, reportId),
    (state: AppState, reportId: string, context?: ReportViewContext) => context,
    getEntryToFilterStateLookup
);

function getEntryToFilterStateLookup(
    entries: ReportEntry[],
    reviewStats: GetReportBindingStatsResponse | undefined,
    documentIds?: string | string[],
    flagState?: FlaggedResponse,
    context?: ReportViewContext): FilterStateLookup {
    const entryToFilterStateLookup: FilterStateLookup = {};
    const documentIdArray = Array.isArray(documentIds) ? documentIds : (!!documentIds ? [documentIds] : undefined);

    for (const entry of entries) {
        let fullyConfirmed = false;
        if (context === ReportViewContext.Published) {
            fullyConfirmed = true;
        }
        else {
            fullyConfirmed = !documentIdArray?.length ?
                !!reviewStats?.entriesConfirmed[entry.id] :
                documentIdArray.every(docId => !!reviewStats?.documentEntries[docId]?.[entry.id]);
        }

        const flagged = !documentIdArray?.length ?
            !!flagState?.entries.includes(entry.id) :
            !!flagState?.entries.includes(entry.id) && flagState.documents.some(flag => documentIdArray.includes(flag.folderId));

        entryToFilterStateLookup[entry.id] = {
            fullyConfirmed: fullyConfirmed,
            flagged: flagged
        }
    }

    return entryToFilterStateLookup;
}

export const createSelectGroupEntries = () => createSelector(
    (state: AppState, reportId: string, groupId: string) => selectOrderedReportEntries(state),
    (state: AppState, reportId: string, groupId: string) => selectReportSchema(state)?.groups.entities[groupId],
    (entries, group): ReportEntry[] => {
        if (!entries || !group) {
            return []
        }

        return entries.filter(x => group.entries.includes(x.id));
    }
);

export interface IReportGroup extends IGroup {
    data?: ReportEntryGroup;
}

export const createSelectGridEntryGroups = () => createSelector(
    (state: AppState, reportId: string, entries: ReportEntry[]) => selectPatchedReportSchemaState(state, reportId),
    (state: AppState, reportId: string, entries: ReportEntry[]) => entries,
    (state, entries): IReportGroup[] => {
        if (!state) {
            return [];
        }

        return state.groups.ids.map(groupId => {
            const group = state.groups.entities[groupId]!;
            const groupEntries = entries.filter(x => group.entries.includes(x.id));
            return {
                key: group.id,
                name: group.label,
                startIndex: !!groupEntries.length ? entries.findIndex(x => x === groupEntries[0]) : -1,
                count: groupEntries.length,
                data: group
            }
        });
    }
)

export const createSelectEntryTitles = () => createSelector(
    (state: AppState, reportId: string) => selectPatchedReportSchemaState(state, reportId),
    (state): string[] => {
        if (!state) {
            return []
        }

        return state.report.entries.map(x => state.entries.entities[x]?.title).filter(x => !!x) as string[];
    }
)

export const createSelectEntryGroupLabels = () => createSelector(
    (state: AppState, reportId: string) => selectPatchedReportSchemaState(state, reportId),
    (state): string[] => {
        if (!state) {
            return []
        }

        return selectAllGroups(state.groups).map(x => x.label);
    }
)

export interface NormalizedState<T> {
    entities: Dictionary<T>;
    ids: string[];
}

export const createSelectClusterDocuments = () => createSelector(
    [
        (state: AppState, folderIds: string[]) => state.documents.indexed,
        (state: AppState, folderIds: string[]) => folderIds
    ],
    (lookup, folderIds) => {
        const state: NormalizedState<ClusterDocument> = { entities: {}, ids: [] };
        for (const folderId of folderIds) {
            const clusterDocument = lookup[folderId]?.clusterDocument;
            if (!!clusterDocument) {
                state.ids.push(folderId);
                state.entities[folderId] = clusterDocument;
            }
        }

        return state;
    }
);

export interface ReportDocument {
    index: number;
    folderId: string;
    clusterDocument: ClusterDocument;
    state: ReportDocumentState;
}

export const createSelectReportDocuments = () => createSelector(
    (state: AppState, clusterId: string, reportDocumentState?: EntityState<ReportDocumentState>) => state.clusters.indexed[clusterId]?.documentsFetchState.fetched,
    (state: AppState, clusterId: string, reportDocumentState?: EntityState<ReportDocumentState>) => state.documents.indexed,
    (state: AppState, clusterId: string, reportDocumentState?: EntityState<ReportDocumentState>) => reportDocumentState,
    (docsFetched, clusterDocuments, reportDocumentsState): NormalizedState<ReportDocument> | undefined => {
        if (!docsFetched || !reportDocumentsState) {
            return;
        }

        const state: NormalizedState<ReportDocument> = {
            entities: {},
            ids: []
        };

        for (const id of reportDocumentsState.ids) {
            const clusterDocument = clusterDocuments[id]?.clusterDocument;
            if (!!clusterDocument) {
                state.ids.push(id as string);
                state.entities[id] = {
                    index: reportDocumentsState.entities[id]!.index,
                    folderId: id as string,
                    clusterDocument: clusterDocument,
                    state: reportDocumentsState.entities[id]!
                }
            }
        }

        return state;
    }
)

export const createSelectDocumentsWithCpml = () => createSelector(
    (state: AppState, clusterId: string) => state.clusters.indexed[clusterId]?.documentsFetchState.fetched,
    (state: AppState, clusterId: string) => state.documents.indexed,
    (state: AppState, clusterId: string) => clusterId,
    (docsFetched, clusterDocuments, clusterId): NormalizedState<ClusterDocument> | undefined => {
        if (!docsFetched) {
            return;
        }

        const state: NormalizedState<ClusterDocument> = {
            entities: {},
            ids: []
        };

        Object.values(clusterDocuments).forEach(doc => {
            if (doc.clusterDocument.hasCpml && doc.clusterDocument.clusterId === clusterId) {
                state.entities[doc.clusterDocument.folderId] = doc.clusterDocument
                state.ids.push(doc.clusterDocument.folderId)
            }
        });

        return state;
    }
)

export const createSelectReportOperationByMetadataPredicate = () => createSelector(
    (state: AppState, predicate: (operationMetadata: OperationMetadata) => boolean) => selectAllReportRemoteOperations(state.reportOperations.remoteOps),
    (state: AppState, predicate: (operationMetadata: OperationMetadata) => boolean) => predicate,

    (remoteOperations, predicate) => {
        if (!remoteOperations || !remoteOperations.length) {
            return [];
        }

        return Object.values(remoteOperations).filter(remoteOp => !!remoteOp.operationMetadata && predicate(remoteOp.operationMetadata))
    }
)

export const selectAllBindingsConfirmed = createSelector(
    [
        (state: AppState, reportId: string) => selectGetReviewBindingStatsState(state, reportId)?.documentBindingsConfirmed,
        (state: AppState, reportId: string) => selectGetReviewDocumentsState(state, reportId)?.documents.ids
    ],
    (confirmationLookup, documentIds): boolean => {
        if (!confirmationLookup || !documentIds || !documentIds.length) {
            return false;
        }

        for (const id of documentIds) {
            if (!confirmationLookup[id]) {
                return false;
            }
        }

        return true;
    }
)

export const createSelectFilteredReportDocuments = () => createSelector(
    [
        selectReportDocumentsState,
        (state: AppState) => state.documents.indexed,
        (state: AppState) => state.reportsSlice.filterAxis,
        (state: AppState) => state.reportsSlice.searchString,
        (state: AppState) => state.reportsSlice.activeFilters,
        (state: AppState) => selectGetReviewBindingStatsState(state, selectReportId(state)),
        (state: AppState) => selectFlaggedDocumentIds(state, selectReportId(state))?.documents
    ],
    (documentsState, documents, filterAxis, searchString, filters, bindingStats, flaggedState): string[] => {
        let documentIds: string[] = documentsState?.ids as string[] ?? [];
        if (filterAxis !== ReportFilterAxis.Documents) {
            return documentIds;
        }

        if (!!searchString) {
            documentIds = documentIds.filter(id => documents[id]?.clusterDocument?.filename.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()));
        }

        if (!!filters.length && !!documentIds.length) {
            const showConfirmed = filters.includes(ReportFilter.Confirmed);
            const showUnconfirmed = filters.includes(ReportFilter.Unconfirmed);
            const showFlagged = filters.includes(ReportFilter.Flagged);
            documentIds = documentIds.filter(id => {
                const isConfirmed = !!bindingStats?.documentBindingsConfirmed[id];
                const isUnconfirmed = !isConfirmed;
                const isFlagged = !!flaggedState?.some(x => x.folderId === id);

                return (isConfirmed && showConfirmed) || (isUnconfirmed && showUnconfirmed) || (isFlagged && showFlagged);
            });
        }

        return documentIds;
    }
)

export const selectIsReportPublishing = createSelector(
    [
        (state: AppState, reportId: string, published?: boolean) => !!published ? selectGetPublishedDocumentsState(state, reportId) : selectGetReviewDocumentsState(state, reportId)
    ],
    (documentsState): boolean => {
        if (!documentsState) {
            return false;
        }

        return !!selectAllDocuments(documentsState.documents).find(x => x.evaluationStatus === DocumentEvaluationStatus.Confirmed);
    }
)

export interface BindingAndEvaluation {
    binding: ReportBinding;
    evaluation?: ReportBindingEvaluationPreview;
    key: string;
}

export const bindingAndEvaluationComparer = (a: BindingAndEvaluation, b: BindingAndEvaluation): number =>
    evaluationReadingOrderComparer(a.evaluation, b.evaluation);

export const evaluationReadingOrderComparer = (a?: ReportBindingEvaluation, b?: ReportBindingEvaluation): number =>
    TokenRangeUtils.readingOrderComparer(a?.tokenRange, b?.tokenRange);

export interface RepublishCandidateDocument {
    folderId: string;
    reason: string;
    evaluationStatus: DocumentEvaluationStatus;
}

export const createSelectDocumentsNeedingRepublishing = () => createSelector(
    (state: AppState, reportId: string) => selectPatchedReportSchemaState(state, reportId)?.report.isPublished,
    (state: AppState, reportId: string) => state.documents.indexed,
    (state: AppState, reportId: string) => selectGetPublishedDocumentsState(state, reportId)?.documents,
    (isPublished, indexedDocuments, reportDocuments): Record<string, RepublishCandidateDocument> | undefined => {
        if (!isPublished || !reportDocuments) {
            return;
        }

        const state: Record<string, RepublishCandidateDocument> = {};

        if (!!reportDocuments) {
            const { selectAll: selectAllReportDocuments } = reportDocumentsAdapter.getSelectors();

            for (const reportDocument of selectAllReportDocuments(reportDocuments)) {
                const evaluationStatus = reportDocument.evaluationStatus ?? DocumentEvaluationStatus.None;
                const hasCpml = indexedDocuments[reportDocument.folderId]?.clusterDocument.hasCpml;

                if (!!reportDocument && hasCpml &&
                    (evaluationStatus === DocumentEvaluationStatus.OutOfDate ||
                        evaluationStatus === DocumentEvaluationStatus.Error)) {
                    let reason = 'The document is out of date. Please republish.';

                    if (evaluationStatus === DocumentEvaluationStatus.Error) {
                        reason = 'The document was not published properly. Please republish.';
                    }

                    state[reportDocument.folderId] = {
                        folderId: reportDocument.folderId,
                        reason,
                        evaluationStatus,
                    };
                }
            }
        }

        return state;
    }
)

export const createSelectInProgressDocumentsForAutoConfirm = () => createSelector(
    [
        (state: AppState, reportId: string, clusterId: string) => state.documents.indexed,
        (state: AppState, reportId: string, clusterId: string) => getClusterById(state, clusterId)?.docIds,
        (state: AppState, reportId: string, clusterId: string) => selectPatchedReportSchemaState(state, reportId)?.report
    ],
    (documents, docsetDocumentIds, report): ClusterDocument[] => {
        if (!docsetDocumentIds) {
            return [];
        }

        // We only want to select progress docs for a published, auto-confirm report
        if (!report?.isPublished || !report.autoPublishDocuments) {
            return [];
        }

        const isProgressDoc = (status: DocumentStatus) =>
            status === DocumentStatus.New ||
            status === DocumentStatus.Ingesting ||
            status === DocumentStatus.Ingested ||
            status === DocumentStatus.Processing;

        const progressDocs: ClusterDocument[] = [];
        for (const docId in docsetDocumentIds) {
            const clusterDocument = documents[docId]?.clusterDocument;
            if (!!clusterDocument && isProgressDoc(clusterDocument.status)) {
                progressDocs.push(clusterDocument);
            }
        }

        return progressDocs;
    }
)

export const selectActiveTrainingEntryIds = createSelector(
    [
        (state: AppState, reportId: string, docsetId: string) => frontendSlice.endpoints.getFeedbackStatus.select({ docsetId: docsetId })(state)?.data?.labelIds,
        (state: AppState, reportId: string, docsetId: string) => selectSearchResultReportEntries(state, reportId)
    ],
    (trainingLabelIds, entries): Record<string, boolean> => {
        if (!trainingLabelIds?.length || !entries.length) {
            return {};
        }

        const training: Record<string, boolean> = {};

        // As an optimization and to handle cases where we may not have bindings for other batches, we assume the label for an
        // entry will match the label for a given piece of feedback if active
        for (const entry of entries) {
            training[entry.id] = trainingLabelIds.includes(entry.labelId);
        }

        return training;
    }
)

export const createSelectIsTrainingActiveForSpan = () => createSelector(
    [
        (state: AppState, docsetId: string, fileId: string, spanId?: string) => frontendSlice.endpoints.getFeedbackStatus.select({ docsetId: docsetId })(state)?.data?.feedback,
        (state: AppState, docsetId: string, fileId: string, spanId?: string) => fileId,
        (state: AppState, docsetId: string, fileId: string, spanId?: string) => spanId
    ],
    (feedbackItems, fileId, spanId) => feedbackItems?.some(x => x.fileId === fileId && ((x.spanId === spanId) || (!x.spanId && !spanId)))
);

export const createSelectUnconfirmedBindingIds = () => createSelector(
    [
        (state: AppState, reportId: string, folderId: string, entryId: string) => selectReportEntryBindings(state, createFolderId(folderId).fileId),
        (state: AppState, reportId: string, folderId: string, entryId: string) => folderId,
        (state: AppState, reportId: string, folderId: string, entryId: string) => entryId,
    ],
    (entryBindings, folderId, entryId) => entryBindings?.entities[entryId]?.bindings.filter(binding => !!binding.spanId && !binding.isConfirmed && !binding.userConfirmed).map(binding => binding.spanId!)
);


export const selectIsReportPublished = (state: AppState) => !!state.reportsSlice.reportId ?
    selectPatchedReportSchemaState(state, state.reportsSlice.reportId)?.report.isPublished :
    undefined;

export const selectReportSchema = (state: AppState) => !!state.reportsSlice.reportId ? selectPatchedReportSchemaState(state, state.reportsSlice.reportId) : undefined;
export const selectReport = (state: AppState) => !!state.reportsSlice.reportId ? selectPatchedReportSchemaState(state, state.reportsSlice.reportId)?.report : undefined;
export const selectEntries = (state: AppState) => !!state.reportsSlice.reportId ? selectPatchedReportSchemaState(state, state.reportsSlice.reportId)?.entries : undefined;
export const selectOrderedReportEntryIds = (state: AppState) => selectReport(state)?.entries;
export const selectReportGroups = (state: AppState) => selectReportSchema(state)?.groups;

export const selectIsReportSchemaLoaded = (state: AppState) => {
    if (!!state.reportsSlice.reportId) {
        const data = selectGetReportSchemaState(state, state.reportsSlice.reportId);
        return !!data;
    }
    return false;
}

export const selectReportViewMode = (state: AppState) => state.reportsSlice.viewMode || ReportViewMode.Document;

export const selectReportContext = (state: AppState) => state.reportsSlice.context;
export const selectReportId = (state: AppState) => state.reportsSlice.reportId!;
export const selectClusterId = (state: AppState) => state.reportsSlice.clusterId!;
export const selectReportName = (state: AppState) => selectReport(state)?.name;
export const selectSelectedLabel = (state: AppState) => state.reportsSlice.selectedLabel;
export const selectSelectedEntry = createSelector(
    [selectEntries, selectSelectedLabel],
    (entries, selectedLabel): ReportEntry | undefined => {
        return !!entries ? selectAllEntries(entries).find(entry => entry.title === selectedLabel) : undefined;
    }
);

export const selectReportDocumentId = (state: AppState) => state.reportsSlice.folderId ?? '';

export const selectPublishedDocumentsState = (state: AppState) => !!state.reportsSlice.reportId ?
    selectGetPublishedDocumentsState(state, state.reportsSlice.reportId)?.documents :
    undefined;

export const selectReportDocumentsState = (state: AppState, context?: ReportViewContext) => {
    context = context ?? state.reportsSlice.context
    return context === ReportViewContext.Published ?
        selectPublishedDocumentsState(state) :
        selectReviewDocumentsState(state)
}

export const selectReportDocumentsView = (state: AppState, context?: ReportViewContext) => {
    context = context ?? state.reportsSlice.context
    return context === ReportViewContext.Published ?
        selectPublishedDocumentsView(state) :
        selectReviewDocumentsView(state)
}

export const selectReportDocument = (state: AppState, folderId: string) => selectReportDocumentsView(state)?.entities[folderId];

const selectPublishedReportDocuments = createSelectReportDocuments();
const selectReviewReportDocuments = createSelectReportDocuments();

export const selectReviewDocumentsView = (state: AppState) => {
    const clusterId = state.reportsSlice.clusterId;
    if (!clusterId) {
        return;
    }

    const reviewDocuments = selectReviewDocumentsState(state);
    if (!reviewDocuments) {
        return;
    }

    return selectReviewReportDocuments(state, clusterId, reviewDocuments);
}

export const selectPublishedDocumentsView = (state: AppState) => {
    const clusterId = state.reportsSlice.clusterId;
    if (!clusterId) {
        return;
    }

    const publishedDocuments = selectPublishedDocumentsState(state);
    if (!publishedDocuments) {
        return;
    }

    return selectPublishedReportDocuments(state, clusterId, publishedDocuments);
}

export const selectReviewDocumentsState = (state: AppState) => !!state.reportsSlice.reportId ?
    selectGetReviewDocumentsState(state, state.reportsSlice.reportId)?.documents :
    undefined;

export const selectReportEvaluationsForCurrentDocument = (state: AppState) => !!state.reportsSlice.reportId && !!state.reportsSlice.fileId ?
    selectGetEvaluationsPreviewState(state, { reportId: state.reportsSlice.reportId, fileId: state.reportsSlice.fileId })?.evaluations :
    undefined;

export const selectReportEvaluations = (state: AppState, fileId: string | undefined) => !!state.reportsSlice.reportId && !!fileId ?
    selectGetEvaluationsPreviewState(state, { reportId: state.reportsSlice.reportId, fileId: fileId })?.evaluations :
    undefined;

export const selectReportEntryBindings = (state: AppState, fileId: string | undefined) => !!state.reportsSlice.reportId && !!fileId ?
    selectPatchedReportBindingsState(state, { reportId: state.reportsSlice.reportId, fileId: fileId })?.entryBindings :
    undefined;

export const selectReportEntryBindingsLoading = (state: AppState, fileId: string | undefined) => !!state.reportsSlice.reportId && !!fileId ?
    !!selectGetBindingsState(state, { reportId: state.reportsSlice.reportId, fileId: fileId })?.isLoading :
    undefined;

export const selectReportEntryBindingsForCurrentDocument = (state: AppState) => !!state.reportsSlice.reportId ?
    selectPatchedReportBindingsState(state, { reportId: state.reportsSlice.reportId, fileId: state.reportsSlice.fileId })?.entryBindings :
    undefined;

export const selectIsDocumentPublished = (state: AppState, folderId: string) => !!selectPublishedDocumentsState(state)?.entities[folderId];
export const selectIsDocumentPublishing = (state: AppState, folderId: string) => selectPublishedDocumentsState(state)?.entities[folderId]?.evaluationStatus === DocumentEvaluationStatus.Confirmed;

export const selectReportLabels = (state: AppState) => state.reportsSlice.reportLabels!;
export const selectReportEntry = (state: AppState, entryId: string) => selectEntries(state)?.entities[entryId];


export const selectIsAnyReportDocumentPublishing = createSelector(
    [
        selectIsReportPublished,
        selectReportDocumentsState
    ],
    (isPublished, documentsState): boolean => {
        if (!isPublished || !documentsState) {
            return false;
        }

        return selectAllDocuments(documentsState).some(x => x.evaluationStatus === DocumentEvaluationStatus.Confirmed);
    }
)

export const createSelectReportLabelIds = () => createSelector(
    (state: AppState) => selectOrderedReportEntries(state),
    (entries): string[] => entries.map(entry => entry.labelId)
)
